import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, unref as _unref, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = [".open", "title", "subtitle"]
const _hoisted_2 = {
  id: "modal-container-element",
  class: "modal-content"
}
const _hoisted_3 = { columns: "2" }
const _hoisted_4 = ["value", "disabled"]
const _hoisted_5 = ["value"]
const _hoisted_6 = ["value"]
const _hoisted_7 = { columns: "1" }
const _hoisted_8 = ["value"]
const _hoisted_9 = { columns: "1" }
const _hoisted_10 = ["value"]
const _hoisted_11 = { columns: "2" }
const _hoisted_12 = ["value"]
const _hoisted_13 = ["value"]
const _hoisted_14 = { columns: "1" }
const _hoisted_15 = ["value"]
const _hoisted_16 = { columns: "2" }
const _hoisted_17 = ["value"]
const _hoisted_18 = ["value"]
const _hoisted_19 = { columns: "1" }
const _hoisted_20 = ["checked"]
const _hoisted_21 = { style: {"display":"flex","align-items":"flex-end","gap":"8px"} }
const _hoisted_22 = ["value", "onTextInputChange"]
const _hoisted_23 = ["onButtonClick"]
const _hoisted_24 = { slot: "footer" }
const _hoisted_25 = ["disabled"]

import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useCreateCatalogService, useUpdateCatalogService } from '../../queries/useCatalogServiceMutation';

import { validateKubernetesName } from '@shell/utils/validators/kubernetes-name';
import { validateSettings } from '../../utils/settings';
import { mergeSettingDefaults } from '../../models/catalogservice/mappers';
import Banner from '@components/Banner/Banner.vue';
import ChartSettings from '../settings/ChartSettings.vue';
import { CatalogService, ChartSetting, CatalogServiceCreateRequest, CatalogServiceUpdateRequest } from '../../models/catalogservice/ui-types';

import isEqual from 'lodash/isEqual';
import sortBy from 'lodash/sortBy';


export default /*@__PURE__*/_defineComponent({
  __name: 'CatalogServiceModal',
  setup(__props, { expose: __expose }) {

const store = useStore() as any;
const t = store.getters['i18n/t'];

// Modal open state
const showModal = ref(false);
const modalMode = ref<'create' | 'edit' | 'view'>('create');

const initialValues = ref<CatalogService | null>(null);

// Form fields (separate from the model to avoid proxy mutation issues)
const catalogServiceName = ref('');
const catalogServiceShortDescription = ref('');
const catalogServiceDescription = ref('');
const catalogServiceChart = ref('');
const catalogServiceChartVersion = ref('');
const catalogServiceAppVersion = ref('');
const catalogServiceIcon = ref('');
const catalogServiceHelmRepo = ref({ name: '', url: '', secret: '' });
const chartSettings = ref<ChartSetting[]>([]);
const catalogServiceSecretTypes = ref<string[]>([]);

const hasAssociatedServices = ref<boolean>(false);

const isEdit = computed(() => modalMode.value === 'edit');
const isView = computed(() => modalMode.value === 'view');

const showAdvancedOptions = ref<boolean>(false);

const {mutateAsync: createCatalogService, isPending: isCreatingCatalogService, isError: createCatalogServiceError, error: createCatalogServiceErrorData} = useCreateCatalogService(store, () => {
  handleSuccess('create');
  closeModal();
});
const {mutateAsync: updateCatalogService, isPending: isUpdatingCatalogService, isError: updateCatalogServiceError, error: updateCatalogServiceErrorData} = useUpdateCatalogService(store, () => {
  handleSuccess('update');
  closeModal();
});

const isDirty = computed(() => {
  return dirtyFields.value.name ||
    dirtyFields.value.shortDescription ||
    dirtyFields.value.description ||
    dirtyFields.value.chart ||
    dirtyFields.value.chartVersion ||
    dirtyFields.value.appVersion ||
    dirtyFields.value.serviceIcon ||
    dirtyFields.value.helmRepo ||
    dirtyFields.value.settings ||
    dirtyFields.value.secretTypes;
 });

const dirtyFields = computed(() => {
  const fields: Partial<
    Record<keyof CatalogServiceCreateRequest, boolean>
  > = {};

  fields.name = catalogServiceName.value !== (initialValues.value?.meta.name || '');
  fields.shortDescription = catalogServiceShortDescription.value !== (initialValues.value?.shortDescription || '');
  fields.description = catalogServiceDescription.value !== (initialValues.value?.description || '');
  fields.chart = catalogServiceChart.value !== (initialValues.value?.chart || '');
  fields.chartVersion = catalogServiceChartVersion.value !== (initialValues.value?.chartVersion || '');
  fields.appVersion = catalogServiceAppVersion.value !== (initialValues.value?.appVersion || '');
  fields.serviceIcon = catalogServiceIcon.value !== (initialValues.value?.serviceIcon || '');
  fields.helmRepo = catalogServiceHelmRepo.value.name !== (initialValues.value?.helmRepo?.name || '') ||
                    catalogServiceHelmRepo.value.url !== (initialValues.value?.helmRepo?.url || '') ||
                    catalogServiceHelmRepo.value.secret !== (initialValues.value?.helmRepo?.secret || '');
  fields.settings = !isEqual(sortBy(chartSettings.value, 'name'), sortBy(initialValues.value?.settings || [], 'name'));
  fields.secretTypes = !isEqual(sortBy(catalogServiceSecretTypes.value), sortBy(initialValues.value?.secretTypes || []));

  return fields;
});

const showDiscardConfirm = ref(false);

const validationPassed = computed(() => {
  if (!catalogServiceName.value) return false;
  if (!catalogServiceShortDescription.value) return false;
  if (!catalogServiceDescription.value) return false;
  if (!catalogServiceChart.value) return false;
  if (!catalogServiceHelmRepo.value.name) return false;
  if (!catalogServiceHelmRepo.value.url) return false;

  const settingsValidation = validateSettings(chartSettings.value);
  if (!settingsValidation) return false;

  const secretTypesValid = catalogServiceSecretTypes.value.every((type) => type && type.trim() !== '');
  if (!secretTypesValid) return false;

  const nameErrors = validateKubernetesName(catalogServiceName.value, '', store.getters, undefined, []);
  return nameErrors.length === 0;
});

const canSave = computed(() => {
  const dirty = isDirty.value;
  const valid = validationPassed.value;
  return dirty && valid && !isCreatingCatalogService.value && !isUpdatingCatalogService.value;
});

function openCreate() {
  modalMode.value = 'create';
  catalogServiceName.value = '';
  catalogServiceShortDescription.value = '';
  catalogServiceDescription.value = '';
  catalogServiceChart.value = '';
  catalogServiceChartVersion.value = '';
  catalogServiceAppVersion.value = '';
  catalogServiceIcon.value = '';
  catalogServiceHelmRepo.value = { name: '', url: '', secret: '' };
  chartSettings.value = [];
  catalogServiceSecretTypes.value = [];
  showModal.value = true;
}

function openEdit(row: CatalogService) {
  modalMode.value = 'edit';
  initialValues.value = row;
  catalogServiceName.value = row.meta.name;
  catalogServiceShortDescription.value = row.shortDescription || '';
  catalogServiceDescription.value = row.description || '';
  catalogServiceChart.value = row.chart || '';
  catalogServiceChartVersion.value = row.chartVersion || '';
  catalogServiceAppVersion.value = row.appVersion || '';
  catalogServiceIcon.value = row.serviceIcon || '';
  catalogServiceHelmRepo.value = {
    name: row.helmRepo?.name || '',
    url: row.helmRepo?.url || '',
    secret: row.helmRepo?.secret || ''
  };
  chartSettings.value = row.settings || [];
  catalogServiceSecretTypes.value = row.secretTypes || [];
  hasAssociatedServices.value = !!row.boundServices;
  showAdvancedOptions.value = !!row.secretTypes && row.secretTypes.length > 0;

  showModal.value = true;
}

function handleModalClose() {
  if (isDirty.value) {
    showDiscardConfirm.value = true;
  } else {
    closeModal();
  }
}

function handleKeepEditing() {
  showDiscardConfirm.value = false;
}

function handleDiscard() {
  showDiscardConfirm.value = false;
  closeModal();
}

function closeModal() {
  // Clear form state before setting showModal = false so that when Lit fires
  // modal-close (which triggers handleModalClose), isDirty is already false
  catalogServiceName.value = '';
  catalogServiceShortDescription.value = '';
  catalogServiceDescription.value = '';
  catalogServiceChart.value = '';
  catalogServiceChartVersion.value = '';
  catalogServiceAppVersion.value = '';
  catalogServiceIcon.value = '';
  catalogServiceHelmRepo.value = { name: '', url: '', secret: '' };
  chartSettings.value = [];
  catalogServiceSecretTypes.value = [];
  showDiscardConfirm.value = false;
  showModal.value = false;
  initialValues.value = null;
  hasAssociatedServices.value = false;
  showAdvancedOptions.value = false;
}

const buildCreateRequest = (): CatalogServiceCreateRequest => {
  const request: CatalogServiceCreateRequest = {
    name: catalogServiceName.value,
    shortDescription: catalogServiceShortDescription.value,
    description: catalogServiceDescription.value,
    chart: catalogServiceChart.value,
    serviceIcon: catalogServiceIcon.value,
    helmRepo: { ...catalogServiceHelmRepo.value },
  };
  if (catalogServiceChartVersion.value) {
    request.chartVersion = catalogServiceChartVersion.value;
  }
  if (catalogServiceAppVersion.value) {
    request.appVersion = catalogServiceAppVersion.value;
  }
  if (catalogServiceIcon.value) {
    request.serviceIcon = catalogServiceIcon.value;
  }
  if (chartSettings.value.length > 0) {
    request.settings = chartSettings.value;
  }
  if (catalogServiceSecretTypes.value.length > 0) {
    request.secretTypes = [...catalogServiceSecretTypes.value];
  }
  return request;
};

const buildUpdateRequest = (): CatalogServiceUpdateRequest => {
  const request: CatalogServiceUpdateRequest = {};

  if (dirtyFields.value.name) {
    request.name = catalogServiceName.value;
  }

  if (dirtyFields.value.description) {
    request.description = catalogServiceDescription.value;
  }

  if (dirtyFields.value.shortDescription) {
    request.shortDescription = catalogServiceShortDescription.value;
  }

  if (dirtyFields.value.chart) {
    request.chart = catalogServiceChart.value;
  }

  if (dirtyFields.value.chartVersion) {
    request.chartVersion = catalogServiceChartVersion.value;
  }

  if (dirtyFields.value.appVersion) {
    request.appVersion = catalogServiceAppVersion.value;
  }

  if (dirtyFields.value.serviceIcon) {
    request.serviceIcon = catalogServiceIcon.value;
  }

  if (dirtyFields.value.helmRepo) {
    request.helmRepo = { ...catalogServiceHelmRepo.value };
  }

  if (dirtyFields.value.settings) {
    request.settings = chartSettings.value;
    request.values = mergeSettingDefaults(
      initialValues.value?.values,
      chartSettings.value
    );
  }

  if (dirtyFields.value.secretTypes) {
    request.secretTypes = [...catalogServiceSecretTypes.value];
  }

  return request;
};

async function onSubmit() {
  if (!validationPassed.value || !isDirty.value || isCreatingCatalogService.value || isUpdatingCatalogService.value) return;

  if (isEdit.value && initialValues.value) {
    const request: CatalogServiceUpdateRequest = buildUpdateRequest();

    await updateCatalogService({ name: initialValues.value.meta.name, request });
  } else {
    const request: CatalogServiceCreateRequest = buildCreateRequest();
    await createCatalogService({request});
  }
}

const handleSuccess = (type: 'create' | 'update') => {
  store.dispatch('growl/success', {
    title:   t(`epinio.growl.catalogServices.${type}.success.title`),
    message: t(`epinio.growl.catalogServices.${type}.success.message`, { name: catalogServiceName.value }),
  });
};

__expose({ openCreate, openEdit });

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("trailhand-modal", {
    ".open": showModal.value,
    ".dismissible": false,
    title: (isView.value || isEdit.value) ? initialValues.value?.meta?.name || 'Catalog Service' : 'Catalog Service',
    subtitle: (isView.value || isEdit.value) ? '' : 'Create New',
    position: "top",
    onModalClose: handleModalClose
  }, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("trailhand-form-card", null, [
        (hasAssociatedServices.value)
          ? (_openBlock(), _createBlock(Banner, {
              key: 0,
              color: "warning",
              label: "This catalog service is currently associated with one or more services. Editing it may cause issues for those services."
            }))
          : _createCommentVNode("", true),
        _cache[21] || (_cache[21] = _createTextVNode()),
        _createElementVNode("trailhand-form-row", _hoisted_3, [
          _createElementVNode("trailhand-text-input", {
            value: catalogServiceName.value,
            label: "Name",
            placeholder: "A Unique Name",
            required: true,
            disabled: isEdit.value,
            onTextInputChange: _cache[0] || (_cache[0] = (e) => { catalogServiceName.value = e.detail.value; })
          }, null, 40, _hoisted_4),
          _cache[14] || (_cache[14] = _createTextVNode()),
          _createElementVNode("trailhand-text-input", {
            value: catalogServiceShortDescription.value,
            label: "Short Description",
            placeholder: "A brief description",
            required: true,
            onTextInputChange: _cache[1] || (_cache[1] = (e) => { catalogServiceShortDescription.value = e.detail.value; })
          }, null, 40, _hoisted_5)
        ]),
        _cache[22] || (_cache[22] = _createTextVNode()),
        _createElementVNode("trailhand-form-row", null, [
          _createElementVNode("trailhand-text-area", {
            value: catalogServiceDescription.value,
            label: "Description",
            placeholder: "A detailed description",
            required: "",
            onTextAreaChange: _cache[2] || (_cache[2] = (e) => { catalogServiceDescription.value = e.detail.value; })
          }, null, 40, _hoisted_6)
        ]),
        _cache[23] || (_cache[23] = _createTextVNode()),
        _createElementVNode("trailhand-form-row", _hoisted_7, [
          _createElementVNode("trailhand-text-input", {
            value: catalogServiceIcon.value,
            label: "Icon URL",
            placeholder: "e.g. registry.example.com/builder:latest",
            onTextInputChange: _cache[3] || (_cache[3] = (e) => { catalogServiceIcon.value = e.detail.value; })
          }, null, 40, _hoisted_8)
        ]),
        _cache[24] || (_cache[24] = _createTextVNode()),
        _createElementVNode("trailhand-form-row", _hoisted_9, [
          _createElementVNode("trailhand-text-input", {
            value: catalogServiceChart.value,
            label: "Chart",
            placeholder: "e.g. mychart",
            required: "",
            onTextInputChange: _cache[4] || (_cache[4] = (e) => { catalogServiceChart.value = e.detail.value; })
          }, null, 40, _hoisted_10)
        ]),
        _cache[25] || (_cache[25] = _createTextVNode()),
        _createElementVNode("trailhand-form-row", _hoisted_11, [
          _createElementVNode("trailhand-text-input", {
            value: catalogServiceChartVersion.value,
            label: "Chart Version",
            placeholder: "1.0.0",
            onTextInputChange: _cache[5] || (_cache[5] = (e) => { catalogServiceChartVersion.value = e.detail.value; })
          }, null, 40, _hoisted_12),
          _cache[15] || (_cache[15] = _createTextVNode()),
          _createElementVNode("trailhand-text-input", {
            value: catalogServiceAppVersion.value,
            label: "App Version",
            placeholder: "1.0.0",
            onTextInputChange: _cache[6] || (_cache[6] = (e) => { catalogServiceAppVersion.value = e.detail.value; })
          }, null, 40, _hoisted_13)
        ]),
        _cache[26] || (_cache[26] = _createTextVNode()),
        _createElementVNode("trailhand-form-row", _hoisted_14, [
          _createElementVNode("trailhand-text-input", {
            value: catalogServiceHelmRepo.value.name,
            label: "Helm Repo Name",
            placeholder: "e.g. my-helm-repo",
            required: "",
            onTextInputChange: _cache[7] || (_cache[7] = (e) => { catalogServiceHelmRepo.value.name = e.detail.value; })
          }, null, 40, _hoisted_15)
        ]),
        _cache[27] || (_cache[27] = _createTextVNode()),
        _createElementVNode("trailhand-form-row", _hoisted_16, [
          _createElementVNode("trailhand-text-input", {
            value: catalogServiceHelmRepo.value.url,
            label: "Helm Repo URL",
            placeholder: "e.g. https://my-helm-repo.com",
            required: true,
            onTextInputChange: _cache[8] || (_cache[8] = (e) => { catalogServiceHelmRepo.value.url = e.detail.value; })
          }, null, 40, _hoisted_17),
          _cache[16] || (_cache[16] = _createTextVNode()),
          _createElementVNode("trailhand-text-input", {
            value: catalogServiceHelmRepo.value.secret,
            label: "Helm Repo Secret",
            placeholder: "e.g. my-helm-repo-secret",
            onTextInputChange: _cache[9] || (_cache[9] = (e) => { catalogServiceHelmRepo.value.secret = e.detail.value; })
          }, null, 40, _hoisted_18)
        ]),
        _cache[28] || (_cache[28] = _createTextVNode()),
        _createVNode(ChartSettings, {
          modelValue: chartSettings.value,
          "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((chartSettings).value = $event))
        }, null, 8, ["modelValue"]),
        _cache[29] || (_cache[29] = _createTextVNode()),
        _createElementVNode("trailhand-form-row", _hoisted_19, [
          _createElementVNode("trailhand-checkbox", {
            checked: showAdvancedOptions.value,
            label: "Show Advanced Options",
            onCheckboxChange: _cache[11] || (_cache[11] = (e) => { showAdvancedOptions.value = e.detail.checked; })
          }, "Show Advanced Options", 40, _hoisted_20)
        ]),
        _cache[30] || (_cache[30] = _createTextVNode()),
        (showAdvancedOptions.value)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
              _cache[18] || (_cache[18] = _createElementVNode("h3", null, "Secret Types", -1)),
              _cache[19] || (_cache[19] = _createTextVNode()),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(catalogServiceSecretTypes.value, (value, index) => {
                return (_openBlock(), _createElementBlock("trailhand-form-row", {
                  key: index,
                  columns: "1"
                }, [
                  _createElementVNode("div", _hoisted_21, [
                    _createElementVNode("trailhand-text-input", {
                      value: value,
                      placeholder: "e.g. database-credentials",
                      style: {"flex":"1"},
                      onTextInputChange: (e) => {
                  const newValue = e.detail.value;
                  catalogServiceSecretTypes.value[index] = newValue;
                }
                    }, null, 40, _hoisted_22),
                    _cache[17] || (_cache[17] = _createTextVNode()),
                    _createElementVNode("trailhand-button", {
                      variant: "destructive",
                      onButtonClick: ($event: any) => (catalogServiceSecretTypes.value = catalogServiceSecretTypes.value.filter((_, i) => i !== index))
                    }, "\n                Remove\n              ", 40, _hoisted_23)
                  ])
                ]))
              }), 128)),
              _cache[20] || (_cache[20] = _createTextVNode()),
              _createElementVNode("div", null, [
                _createElementVNode("trailhand-button", {
                  variant: "alternate",
                  onButtonClick: _cache[12] || (_cache[12] = ($event: any) => (catalogServiceSecretTypes.value.push('')))
                }, "\n              Add Secret Type\n            ", 32)
              ])
            ], 64))
          : _createCommentVNode("", true)
      ]),
      _cache[31] || (_cache[31] = _createTextVNode()),
      (_unref(createCatalogServiceError) || _unref(updateCatalogServiceError))
        ? (_openBlock(), _createBlock(Banner, {
            key: 0,
            color: "error",
            label: _unref(createCatalogServiceErrorData)?.message || _unref(updateCatalogServiceErrorData)?.message || _unref(t)('epinio.catalogservices.errors.save')
          }, null, 8, ["label"]))
        : _createCommentVNode("", true)
    ]),
    _cache[37] || (_cache[37] = _createTextVNode()),
    _createElementVNode("div", _hoisted_24, [
      (isView.value)
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
            _createElementVNode("trailhand-button", {
              variant: "secondary",
              class: "mr-10",
              onButtonClick: closeModal
            }, "\n          Close\n        ", 32),
            _cache[32] || (_cache[32] = _createTextVNode()),
            _createElementVNode("trailhand-button", {
              variant: "primary",
              onButtonClick: _cache[13] || (_cache[13] = ($event: any) => (modalMode.value = 'edit'))
            }, "\n          Edit Configuration\n        ", 32)
          ], 64))
        : (showDiscardConfirm.value)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
              _cache[33] || (_cache[33] = _createElementVNode("span", { class: "discard-message" }, "You have unsaved changes.", -1)),
              _cache[34] || (_cache[34] = _createTextVNode()),
              _createElementVNode("trailhand-button", {
                variant: "secondary",
                class: "mr-10",
                onButtonClick: handleKeepEditing
              }, "\n          Keep Editing\n        ", 32),
              _cache[35] || (_cache[35] = _createTextVNode()),
              _createElementVNode("trailhand-button", {
                variant: "destructive",
                onButtonClick: handleDiscard
              }, "\n          Discard\n        ", 32)
            ], 64))
          : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
              _createElementVNode("trailhand-button", {
                variant: "secondary",
                class: "mr-10",
                onButtonClick: handleModalClose
              }, "\n          Cancel\n        ", 32),
              _cache[36] || (_cache[36] = _createTextVNode()),
              _createElementVNode("trailhand-button", {
                variant: "primary",
                disabled: !canSave.value,
                onButtonClick: onSubmit
              }, _toDisplayString(isEdit.value ? (_unref(isUpdatingCatalogService) ? _unref(t)('generic.updating') : _unref(t)('generic.save')) : (_unref(isCreatingCatalogService) ? _unref(t)('generic.creating') : _unref(t)('generic.create'))), 41, _hoisted_25)
            ], 64))
    ])
  ], 40, _hoisted_1))
}
}

})